// combatpro/damage/damageEngine.js
//
// Combat PRO - Damage Engine (Stage 47: pure HP-mutation calculations).
//
// Pure, UI-independent damage / healing logic extracted from the damage runtime
// in battletracker.html (the read-only master spec). No React, no DOM, no
// context, no browser APIs, no side effects. It receives primitives (and, for the
// mass helpers, a read-only monsters array) and returns plain data; it mutates
// nothing and owns no state. This is the central HP-mutation primitive set that
// the manual damage/heal handlers, the mass handlers, and - later - the Spell
// Casting and Attack flows all reuse.
//
// Source mapping (battletracker.html):
//   - applyDamage(currentHP, amount)        the HP write in dealDamage (7741):
//                                           Math.max(0, currentHP - amount). Raw
//                                           (no type/resistance); single-target
//                                           damage applies the raw amount.
//   - applyHealing(currentHP, maxHP, amount) the HP write in healDamage (7783):
//                                           Math.min(maxHP, currentHP + amount).
//                                           Over-heal prevention via the maxHP cap.
//   - clampHP(hp, maxHP)                     the 0..maxHP clamp used throughout.
//   - isDead(oldHP, newHP)                   the death-detection test reused by
//                                           dealDamage (7746), applyMassDamage
//                                           (8763), and applyDamageToTarget (7124):
//                                           oldHP > 0 AND newHP === 0 (the
//                                           transition into death this hit).
//   - damageResult(currentHP, amount)        convenience packaging of the single
//                                           damage hit -> { newHP, died }.
//   - computeFinalDamage(amount, damageType, resistances, immunities,
//                        vulnerabilities)
//                                           the resistance / immunity / vulnerability
//                                           mitigation in applyMassDamage (8745-8756)
//                                           and calculateStandaloneResistance
//                                           (7069-7100): immunity -> 0, resistance
//                                           (or nonmagical vs a physical type) ->
//                                           floor(amount / 2), vulnerability ->
//                                           amount * 2, else the full amount.
//   - massDamageResults(monsters, targets, amount, damageType)
//                                           the per-target loop of applyMassDamage
//                                           (8732-8775) for MONSTER targets only,
//                                           returning result rows (no mutation).
//                                           Player targets are the Players Runtime
//                                           (deferred) and are not handled here.
//   - massHealToMaxResults(monsters, activeMonsterCount)
//                                           the heal-to-max loop of healAllToMax
//                                           (8593-8600): every active monster with
//                                           maxHP > 0 -> newHP = maxHP.
//
// NOT here (interface only / deferred): the combat log, vi_addLastAction (Last
// Actions), saveState / cloud, updatePanel and HP-bar flash DOM, the mass-damage
// modal and its player targeting (Players Runtime), morale rules (moraleEngine -
// the handlers call the existing morale handlers; rules are not duplicated here),
// the encounter summary (recomputed by encounterEngine from state), and the
// spell-driven applyDamage(4464) / weapon applyDamageToTarget(7110) execution
// paths (Spell Casting / Attack - they will reuse these primitives later). No
// optional chaining. No em dash.

// Clamp an HP value into the inclusive 0..maxHP range.
export function clampHP(hp, maxHP) {
  return Math.max(0, Math.min(maxHP, hp));
}

// Apply raw damage to a current HP value, flooring at 0.
export function applyDamage(currentHP, amount) {
  return Math.max(0, (currentHP || 0) - amount);
}

// Apply healing to a current HP value, capped at maxHP (over-heal prevention).
export function applyHealing(currentHP, maxHP, amount) {
  return Math.min(maxHP, (currentHP || 0) + amount);
}

// Death detection: true only when this change took a living monster (oldHP > 0)
// exactly to 0. Healing and already-dead monsters never report a new death.
export function isDead(oldHP, newHP) {
  return oldHP > 0 && newHP === 0;
}

// Package a single raw-damage hit: the new HP and whether it caused death.
export function damageResult(currentHP, amount) {
  const oldHP = currentHP || 0;
  const newHP = applyDamage(oldHP, amount);
  return { newHP, died: isDead(oldHP, newHP) };
}

// Resistance / immunity / vulnerability mitigation for a typed damage amount.
// Mirrors the legacy mass-damage calculation and the standalone resistance
// calculator exactly: immune (the type, or "all damage") -> 0; resistant (the
// type, or "nonmagical" against a physical type) -> half (floored); vulnerable
// (the type) -> double; otherwise the full amount. Priority is immunity ->
// resistance -> vulnerability -> normal, matching calculateStandaloneResistance
// (battletracker.html 7069-7100). An empty damageType means untyped damage and
// returns the full amount (this also avoids the empty-string substring match
// that would otherwise read as a universal immunity).
//
// Stage 76 (Resistance View): the vulnerabilities parameter is an additive 5th
// argument. Existing four-argument callers are unaffected - vulnerabilities is
// undefined for them, ("" ).includes(dt) is false, and the function falls
// through to the original return, byte-for-byte identical to before.
export function computeFinalDamage(amount, damageType, resistances, immunities, vulnerabilities) {
  const dt = (damageType || "").toLowerCase();
  if (!dt) { return amount; }
  const res = (resistances || "").toLowerCase();
  const imm = (immunities || "").toLowerCase();
  if (imm.includes(dt) || imm.includes("all damage")) { return 0; }
  const physical = (dt === "slashing" || dt === "piercing" || dt === "bludgeoning");
  if (res.includes(dt) || (res.includes("nonmagical") && physical)) { return Math.floor(amount / 2); }
  const vuln = (vulnerabilities || "").toLowerCase();
  if (vuln.includes(dt)) { return amount * 2; }
  return amount;
}

// Per-target results for a mass damage application over MONSTER target indices.
// Pure: reads the monsters array, returns one row per target, mutates nothing.
// Each row carries the data the caller needs to commit HP immutably and trigger
// the existing morale handlers (oldHP / newHP / died). Player targets are not
// handled here (Players Runtime, deferred).
export function massDamageResults(monsters, targets, amount, damageType) {
  const rows = [];
  for (let t = 0; t < targets.length; t++) {
    const index = targets[t];
    const monster = monsters[index];
    if (!monster) { continue; }
    const oldHP = monster.currentHP || 0;
    const finalDamage = computeFinalDamage(amount, damageType, monster.resistances, monster.immunities);
    const newHP = applyDamage(oldHP, finalDamage);
    rows.push({ index, oldHP, newHP, finalDamage, died: isDead(oldHP, newHP) });
  }
  return rows;
}

// Per-target results for healing every active monster to maximum HP. Pure: reads
// the monsters array, returns one row per healed monster (maxHP > 0), mutates
// nothing. Healing never causes death, so no died flag is reported.
export function massHealToMaxResults(monsters, activeMonsterCount) {
  const rows = [];
  let limit = activeMonsterCount;
  if (limit > monsters.length) { limit = monsters.length; }
  for (let i = 0; i < limit; i++) {
    if (monsters[i].maxHP > 0) {
      rows.push({ index: i, newHP: monsters[i].maxHP });
    }
  }
  return rows;
}
